import { Component, EventEmitter, Input, OnChanges, Output, SecurityContext, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import HtmlDiff from 'htmldiff-js';

@Component({
  selector: 'shared-components-content-comparison',
  templateUrl: './content-comparison.component.html',
  styleUrls: ['./content-comparison.component.scss']
})
export class ContentComparisonComponent implements OnChanges {
  @Input() heading = '';
  @Input() originalContent = '';
  @Input() modifiedContent = '';
  @Input() originalLabel = 'Original';
  @Input() modifiedLabel = 'Modified';
  @Input() preformatted = false;
  @Output() diffComputed = new EventEmitter<{ additions: number; removals: number }>();

  originalDiffHtml: SafeHtml = '';
  modifiedDiffHtml: SafeHtml = '';
  additions = 0;
  removals = 0;
  hasContent = false;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['originalContent'] || changes['modifiedContent'] || changes['preformatted']) {
      this.computeDiff();
    }
  }

  private computeDiff(): void {
    const rawOriginal = this.originalContent ?? '';
    const rawModified = this.modifiedContent ?? '';
    const original = this.preformatted
      ? this.htmlEncode(rawOriginal)
      : (this.sanitizer.sanitize(SecurityContext.HTML, rawOriginal) ?? '');
    const modified = this.preformatted
      ? this.htmlEncode(rawModified)
      : (this.sanitizer.sanitize(SecurityContext.HTML, rawModified) ?? '');
    this.hasContent = original.trim().length > 0 || modified.trim().length > 0;
    const raw = HtmlDiff.execute(original, modified);
    this.additions = this.countTag(raw, 'ins');
    this.removals = this.countTag(raw, 'del');
    this.originalDiffHtml = this.sanitizer.bypassSecurityTrustHtml(this.removeTag(raw, 'ins'));
    this.modifiedDiffHtml = this.sanitizer.bypassSecurityTrustHtml(this.removeTag(raw, 'del'));
    this.diffComputed.emit({ additions: this.additions, removals: this.removals });
  }

  private htmlEncode(content: string): string {
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private countTag(html: string, tag: string): number {
    const matches = html.match(new RegExp(`<${tag}(\\s[^>]*)?>`, 'gi'));
    return matches ? matches.length : 0;
  }

  private removeTag(html: string, tag: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll(tag).forEach(el => el.remove());
    return doc.body.innerHTML;
  }
}
